import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api/client.js';
import { useTranslation } from '../i18n/I18nContext.jsx';
import PageMeta from '../components/PageMeta.jsx';

const TOOL_SCOPE_OPTIONS = ['safe', 'full'];

function CodeBlock({ value, t }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ position: 'relative', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '0.9rem 3rem 0.9rem 1rem', fontFamily: 'monospace', fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
      {value}
      <button
        onClick={copy}
        aria-label={copied ? t('connector.enrollment.copiedLabel') : t('connector.enrollment.copyLabel')}
        aria-live="polite"
        style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.2rem 0.6rem', fontSize: '0.75rem', color: 'var(--color-muted)' }}
      >
        {copied ? t('connector.enrollment.copied') : t('connector.enrollment.copy')}
      </button>
    </div>
  );
}

function ConnectorRow({ connector, onRevoke, onRename, t }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(connector.name || '');
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef(null);

  const age = connector.lastHeartbeatMs
    ? Math.round((Date.now() - connector.lastHeartbeatMs) / 1000)
    : null;

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  function handleSave() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== connector.name) onRename(connector.connectorId, trimmed);
    setEditing(false);
  }

  const displayName = connector.name || connector.connectorId;

  return (
    <tr>
      <td style={{ padding: '0.6rem 1rem 0.6rem 0', fontSize: '0.9rem' }}>
        {editing ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
              style={{ background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '0.2rem 0.5rem', fontSize: '0.85rem', width: '10rem' }}
            />
            <button className="btn-link" onClick={handleSave} style={{ fontSize: '0.75rem' }}>
              {t('connector.activeConnectors.saveName')}
            </button>
            <button className="btn-link" onClick={() => setEditing(false)} style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              {t('connector.activeConnectors.cancelEdit')}
            </button>
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{displayName}</span>
            <button
              className="btn-link"
              onClick={() => { setDraft(connector.name || ''); setEditing(true); }}
              style={{ fontSize: '0.75rem' }}
            >
              {t('connector.activeConnectors.editName')}
            </button>
          </span>
        )}
        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', fontFamily: 'monospace', marginTop: '0.15rem' }}>
          {connector.connectorId}
        </div>
      </td>
      <td style={{ padding: '0.6rem 1rem' }}>
        <span className={`badge ${connector.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
          {connector.status}
        </span>
      </td>
      <td style={{ padding: '0.6rem 1rem', color: 'var(--color-muted)', fontSize: '0.85rem' }}>
        {age !== null ? t('connector.activeConnectors.agoSuffix', { seconds: age }) : t('connector.activeConnectors.noHeartbeat')}
      </td>
      <td style={{ padding: '0.6rem 0', textAlign: 'right' }}>
        {confirming ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}>
            <span style={{ color: 'var(--color-danger)' }}>
              {t('connector.activeConnectors.revokeConfirm', { name: displayName })}
            </span>
            <button
              className="btn-danger-sm"
              onClick={() => { onRevoke(connector.connectorId); setConfirming(false); }}
            >
              {t('connector.activeConnectors.revokeCta')}
            </button>
            <button className="btn-link" onClick={() => setConfirming(false)} style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
              {t('connector.activeConnectors.cancelEdit')}
            </button>
          </span>
        ) : (
          <button
            className="btn-link"
            onClick={() => setConfirming(true)}
            style={{ fontSize: '0.8rem', color: 'var(--color-danger)' }}
            aria-label={t('connector.activeConnectors.revokeAriaLabel')}
          >
            {t('connector.activeConnectors.revokeCta')}
          </button>
        )}
      </td>
    </tr>
  );
}

function ToolScopeEditor({ value, onChange, saving, t }) {
  const descriptions = {
    safe: t('connector.toolScope.safeDesc'),
    full: t('connector.toolScope.fullDesc'),
  };
  const labels = {
    safe: t('connector.toolScope.safe'),
    full: t('connector.toolScope.full'),
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <select
          className="dash-scope-select"
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={saving}
          aria-label={t('connector.toolScope.label')}
        >
          {TOOL_SCOPE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{labels[opt]}</option>
          ))}
        </select>
        {saving && <span style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>{t('connector.toolScope.saving')}</span>}
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-muted)', margin: '0.35rem 0 0', lineHeight: 1.4 }}>
        {descriptions[value]}
      </p>
    </div>
  );
}

export default function ConnectorPage() {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [enrolledId, setEnrolledId] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [error, setError] = useState(null);
  const [toolScope, setToolScope] = useState('safe');
  const [scopeSaving, setScopeSaving] = useState(false);
  const [scopeError, setScopeError] = useState(null);

  const fetchData = useCallback(() => {
    Promise.all([
      apiFetch('/onboard/connectors'),
      apiFetch('/account/status').catch(() => null),
    ])
      .then(([c, status]) => {
        setConnectors(c.connectors ?? []);
        if (status?.toolScope) setToolScope(status.toolScope);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function generateCode() {
    setEnrolling(true);
    setEnrollmentCode(null);
    setError(null);
    try {
      const connectorId = `connector-${Date.now()}`;
      const res = await apiFetch('/onboard/connectors', {
        method: 'POST',
        body: JSON.stringify({ connectorId }),
      });
      setEnrollmentCode(res.enrollmentCode);
      setEnrolledId(connectorId);
      setTenantId(res.tenantId);
      fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setEnrolling(false);
    }
  }

  async function handleRevoke(connectorId) {
    try {
      await apiFetch('/onboard/connectors/revoke', {
        method: 'POST',
        body: JSON.stringify({ connectorId }),
      });
      setConnectors(prev => prev.map(c =>
        c.connectorId === connectorId ? { ...c, status: 'revoked' } : c
      ));
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleRename(connectorId, newName) {
    try {
      await apiFetch('/onboard/connectors/name', {
        method: 'PUT',
        body: JSON.stringify({ connectorId, name: newName }),
      });
      setConnectors(prev => prev.map(c =>
        c.connectorId === connectorId ? { ...c, name: newName } : c
      ));
    } catch (_) { /* silent */ }
  }

  async function handleScopeChange(newScope) {
    setScopeSaving(true);
    setScopeError(null);
    try {
      await apiFetch('/account/tool-scope', {
        method: 'PUT',
        body: JSON.stringify({ toolScope: newScope }),
      });
      setToolScope(newScope);
    } catch (e) {
      setScopeError(e.message);
    } finally {
      setScopeSaving(false);
    }
  }

  // Filter out revoked connectors for display
  const activeConnectors = connectors.filter(c => c.status !== 'revoked');

  return (
    <>
      <PageMeta
        title={t('connector.meta.title')}
        description={t('connector.meta.description')}
        path="/connector"
      />

      <main id="main-content" className="page">
        <h1 className="page-title">{t('connector.pageTitle')}</h1>

        {error && (
          <div className="card" role="alert" style={{ color: 'var(--color-danger)', marginBottom: '1.5rem' }}>
            {t('connector.errorGeneric', { error })}
          </div>
        )}

        {/* Tool scope */}
        <section aria-labelledby="tool-scope-heading" className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 id="tool-scope-heading" style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '1rem', marginTop: 0 }}>
            {t('connector.toolScope.heading')}
          </h2>
          <ToolScopeEditor
            value={toolScope}
            onChange={handleScopeChange}
            saving={scopeSaving}
            t={t}
          />
          {scopeError && (
            <div role="alert" style={{ color: 'var(--color-danger)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {t('connector.toolScope.error', { error: scopeError })}
            </div>
          )}
        </section>

        {/* Active connectors */}
        <section aria-labelledby="active-connectors-heading" className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 id="active-connectors-heading" style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1rem', marginTop: 0 }}>
            {t('connector.activeConnectors.heading')}
            {activeConnectors.length > 0 && (
              <span style={{ color: 'var(--color-muted)', fontWeight: 400 }}> {t('connector.activeConnectors.count', { count: activeConnectors.length })}</span>
            )}
          </h2>
          {loading && <div className="spinner" role="status" aria-label={t('connector.loading')} />}
          {!loading && activeConnectors.length === 0 && (
            <p style={{ color: 'var(--color-muted)', fontSize: '0.9rem', margin: 0 }}>
              {t('connector.activeConnectors.empty')}
            </p>
          )}
          {!loading && activeConnectors.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label={t('connector.activeConnectors.ariaLabel')}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th scope="col" style={{ textAlign: 'left', padding: '0.4rem 1rem 0.6rem 0', color: 'var(--color-muted)', fontSize: '0.8rem', fontWeight: 600 }}>{t('connector.activeConnectors.colName')}</th>
                  <th scope="col" style={{ textAlign: 'left', padding: '0.4rem 1rem', color: 'var(--color-muted)', fontSize: '0.8rem', fontWeight: 600 }}>{t('connector.activeConnectors.colStatus')}</th>
                  <th scope="col" style={{ textAlign: 'left', padding: '0.4rem 1rem', color: 'var(--color-muted)', fontSize: '0.8rem', fontWeight: 600 }}>{t('connector.activeConnectors.colLastSeen')}</th>
                  <th scope="col" style={{ width: '1%' }} />
                </tr>
              </thead>
              <tbody>
                {activeConnectors.map(c => (
                  <ConnectorRow
                    key={c.connectorId}
                    connector={c}
                    onRevoke={handleRevoke}
                    onRename={handleRename}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Add new connector */}
        <section aria-labelledby="enroll-heading" className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 id="enroll-heading" style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '1rem', marginTop: 0 }}>
            {t('connector.enroll.heading')}
          </h2>
          <p style={{ color: 'var(--color-muted)', fontSize: '0.9rem', margin: '0 0 1rem' }}>
            {t('connector.enroll.description')}
          </p>
          <button className="btn-primary" onClick={generateCode} disabled={enrolling} aria-busy={enrolling}>
            {enrolling ? t('connector.enroll.generating') : t('connector.enroll.generateCta')}
          </button>
        </section>

        {/* Enrollment result */}
        {enrollmentCode && (
          <section aria-labelledby="enrollment-heading" className="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--color-primary)' }}>
            <h2 id="enrollment-heading" style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1rem', marginTop: 0 }}>
              {t('connector.enrollment.heading')}
            </h2>
            <CodeBlock value={enrollmentCode} t={t} />
            <h3 style={{ marginTop: '1.5rem', fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              {t('connector.enrollment.setupHeading')}
            </h3>
            <ol style={{ color: 'var(--color-muted)', fontSize: '0.9rem', paddingLeft: '1.2rem', lineHeight: 1.8, margin: 0 }}>
              <li>{t('connector.enrollment.step1')}</li>
              <li>{t('connector.enrollment.step2')}
                <div style={{ marginTop: '0.5rem' }}>
                  <CodeBlock value={`CLAWDIUS_TENANT_ID=${tenantId}\nCLAWDIUS_CONNECTOR_ID=${enrolledId}\nCLAWDIUS_ENROLLMENT_CODE=${enrollmentCode}\nCLAWDIUS_BRIDGE_URL=${import.meta.env.VITE_API_BASE_URL}`} t={t} />
                </div>
              </li>
              <li style={{ marginTop: '0.5rem' }}>{t('connector.enrollment.step3')}</li>
              <li>{t('connector.enrollment.step4')}</li>
            </ol>
          </section>
        )}
      </main>
    </>
  );
}
